import { isEqual, once } from 'lodash-es'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

//region: selector hooks
/**
 * {@link useSelector} with deep comparison. Use it when you were to return a new object or array from a selector.
 * @param {function} selector The selector function you would pass to {@link useSelector()}
 */
export function useObjSelector(selector) { return useSelector(selector, isEqual) }

/**
 * A state selector with deep comparison (like {@link useObjSelector()}), which auto-fetches data if there's nothing there.
 * @param selector {function} What you would pass to {@link useObjSelector()}; usually this is a `getXYZ()` function
 * @param fetcher {function} What retrieves data from the server; this usually calls a `load()` action under the hood, and is called `fetchXYZ()`
 * @param key {string} Something unique to identify this pair of functions. This is used to create a "cache" of fetcher functions, so they're not called more than once in case the data returned from the server is, indeed, empty. This is needed because, usually, we can't trust neither function to have an internal name.
 * @returns {*}
 */
export function useObjSelectorOrFetch(selector, fetcher, key) {
	const dispatch = useDispatch()

	const data = useObjSelector(selector)
	// console.debug(`Trying to load/fetch ${key}; current state has ${data.length} entries`)
	if (data.length == 0) {
		//won't try to request it again in the lifetime of this app instance
		useObjSelectorOrFetch.registerOnceFunction(dispatch, fetcher, key)()
	}

	return data
}

/** This registry is a way to have a global list of fetch functions which can only be called once; this way, we can call a selector from anywhere and even then, all those calls will refer to the same once() function, not trying to fetch data repeatedly if it's intentionally empty
 * @protected */
useObjSelectorOrFetch.registry = {}
/** Resets the hook's registry upon logout, so new request can be made */
useObjSelectorOrFetch.logout = () => useObjSelectorOrFetch.registry = {}
/** @protected */
useObjSelectorOrFetch.registerOnceFunction = (dispatch, fetcher, key) => {
	if (!(key in useObjSelectorOrFetch.registry)) {
		// console.debug(`Registering once-fetcher for ${key}`)
		useObjSelectorOrFetch.registry[key] = once(() => /*console.debug(`Running once-fetcher for ${key}`) ||*/ dispatch(fetcher))
	}
	return useObjSelectorOrFetch.registry[key]
}

/**
 * Looks for the current entry of a given state slice; if it matches the URL ID, cool! Otherwise, fetches it again and returns it.
 * Implements a "light cache" pattern where we store the complete version of the current entry, in case it's used
 * again in a child screen or if the user decides to revisit it right after going back to the list.
 * @param getCurrent {function} Stuff like `getCurrentItem`
 * @param fetch {function} Stuff like `fetchItem`, `fetchOrder`
 * @returns {?*}
 */
export function useCurrentEntry(getCurrent, fetch) {
	const { id }   = useParams()
	const dispatch = useDispatch()
	let entry      = useSelector(getCurrent)

	entry = entry && entry?.id == id? entry : null
	useEffect(() => {
		if (!entry) {
			dispatch(fetch(id))
		}
	}, [entry, id, fetch, dispatch])

	return entry
}
//endregion

//region: effect hooks
/**
 * Same as {@link useEffect}, but without unmount effect - you don't have to bother not returning anything.
 * @see useCleanup
 * @param effect {function}
 * @param deps {array}
 */
export function usePlainEffect(effect, deps = []) {
	useEffect(() => { effect() }, deps) // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * Runs an `effect` function only if `test` passes.
 * @param test {boolean}
 * @param effect {function}
 * @param deps {array}
 */
export function useEffectIf(test, effect, deps = []) {
	useEffect(() => {
		if (test) {
			return effect()
		}
	}, [test, ...deps]) // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * Runs an effect... only once :)
 * @see https://github.com/streamich/react-use/blob/master/src/useEffectOnce.ts
 * @param effect {function}
 */
export function useEffectOnce(effect) {
	useEffect(effect, []) // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * Runs an `effect` function only if `test` passes... and only once :)
 * @see useEffectIf
 * @see useEffectOnce
 * @param test {boolean}
 * @param effect {function}
 */
export function useEffectOnceIf(test, effect) {
	useEffectIf(test, effect, [])
}

/**
 * Runs an effect without an unmount effect... only once :)
 * @see https://github.com/streamich/react-use/blob/master/src/useEffectOnce.ts
 * @see usePlainEffect
 * @see useEffectOnce
 * @param effect {function}
 */
export function usePlainEffectOnce(effect) {
	usePlainEffect(effect, []) // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * Runs an effect without an unmount effect, only if `test` passes... and only once :)
 * @see usePlainEffect
 * @see useEffectOnce
 * @see useEffectIf
 * @see useEffectOnceIf
 * @param test {boolean}
 * @param effect {function}
 */
export function usePlainEffectOnceIf(test, effect) {
	useEffectOnceIf(test, () => { effect() })
}

/**
 * Even shorter version of {@link useEffect}/{@link usePlainEffect}, which simply calls {@link useDispatch}.
 * @param whatToDispatch {?function} if not given, it's noop - useful if it depends on auth checks
 * @param deps
 */
export function useDispatchEffect(whatToDispatch = null, deps = []) {
    const dispatch = useDispatch()
    usePlainEffect(() => whatToDispatch? dispatch(whatToDispatch) : null, deps)
}

/**
 * Even shorter version of {@link useEffect}/{@link usePlainEffect}, mashed with {@link useEffectIf} - calls {@link useDispatch} if `test` passes.
 * @param test {boolean}
 * @param whatToDispatch {?function} if not given, it's noop - useful if it depends on auth checks
 * @param deps
 */
export function useDispatchEffectIf(test, whatToDispatch = null, deps = []) {
    const dispatch = useDispatch()
    usePlainEffect(() => {
		if (test && whatToDispatch) {
            dispatch(whatToDispatch)
        }
    }, [test, ...deps])
}

// https://stackoverflow.com/a/54096391/102960
function useDeepDependency(value) {
	const ref = useRef() // it can be done by using useMemo as well but useRef is rather cleaner and easier

	if (!isEqual(value, ref.current)) {
		ref.current = value
	}

	return ref.current
}

export function useDeepDependencies(deps = []) {
	return deps.map(useDeepDependency)
}

export function useDeepMemo(generator, deps = []) {
	return useMemo(generator, useDeepDependencies(deps)) // eslint-disable-line react-hooks/exhaustive-deps
}

export function useDeepEffect(effect, deps = []) {
	return useEffect(effect, useDeepDependencies(deps)) // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * Sugar for {@link useEffect} without the effect, just the cleanup phase - a func that just returns a func is weird.
 * @see usePlainEffect
 * @param cleanup {function}
 * @param deps {array}
 */
export function useCleanup(cleanup, deps = []) {
	useEffect(() => cleanup, deps) // eslint-disable-line react-hooks/exhaustive-deps
}
//endregion

/**
 * Returns a dirty function that uses a faux state to force a rerender of the component.
 * @return {function(): void}
 */
export function useForcedRefresher() {
	const [, refresher] = useState(0) //dirty way to rerender ¯\_(ツ)_/¯
	return () => refresher(x => x + 1) //bumps the faux state var
}

/**
 * A dirty way to force a rerender on components. Actually, less dirty since it's now encapsulated, yey :))
 * @param interval {MS|int} How much in the future should this execute?
 * @param check {any} When true, enables the refresh schedule. Also used as {@link useEffect}'s dependency.
 * @param single {boolean} If somehow this will reschedule itself once it fires (a change in deps would be enough), or it only needs to be fired a single time
 * @param extraDeps {array} Extra dependencies (for useEffect)  it should append
 */
export function useScheduledRefresh(interval, check, single = false, extraDeps = []) {
	const refresher = useForcedRefresher()
	useEffect(() => {
		if (check) {
			const setSchedule = single? 'setTimeout' : 'setInterval'
			const handle      = window[setSchedule](refresher, interval)
			return () => window.clearInterval(handle) //yep, it works interchangeably
		}
	}, [check].concat(extraDeps)) // eslint-disable-line react-hooks/exhaustive-deps
}

//region: state hooks
/**
 * @typedef {Object} BoolState
 * @property {boolean} is
 * @property {boolean} isnt
 * @property {function():void} on
 * @property {function():void} off
 * @property {function():void} flip Same as {@link toggle}, but receives no arguments - useful for passing the function directly to event handlers
 * @property {function(v: ?boolean = undefined):void} toggle
 */
/**
 * Sugar-coating on top of `useState`, returning a state prop and two toggle functions: on and off.
 * @param def=false {boolean} The default value to use
 * @return {BoolState}
 */
export function useBoolState(def = false) {
	def = !!def //in case we receive a simple test that ends up on non-boolean but still truthy values
	const [is, setter] = useState(def)
	return {
		is,
		isnt  : !is,
		not   : !is,
		on    : () => setter(true),
		off   : () => setter(false),
		flip  : () => setter(!is),
		toggle: (v = undefined) => setter(v === undefined? !is : !!v),
	}
}

/**
 * Same as {@link useBoolState}, but with the common modal prop names instead
 * @example ```
 * <Btn onClick={modal.doOpen}>Open modal</Btn>
 * <ConfirmModal {...modal}>Really?</ConfirmModal>
 * ```
 * @return {{show: boolean, doOpen: function, doClose: function}}
 */
export function useModalState() {
	const props = useBoolState()
	return {
		show   : props.is,
		doClose: props.off,
		doOpen : props.on,
	}
}

/**
 * Cleaner way to use state hooks: instead of having the verbose `[someVariable, setSomeVariable]`, you just define a simple const and uses its getter/setter props :)
 *
 * Extra features:
 * - if the `initialState` is an object, the result will also include an easier to use `merge()` method, to merge into it new/existing properties, according to [React patterns](https://react.dev/reference/react/useState#updating-objects-and-arrays-in-state).
 * - an idea from react-use/useDefault: if the second argument is given, it's returned whenever the main value is set to null/undefined (or empty/falsey with the last argument as true).
 * - if an input event is given to `set()`, it automatically extracts its `target.value`
 * - `set()` is able to extract the value directly from an input's event (`e.target.value`), and also returns that, so there's no need to wait for the state to settle to work with it on complex setters
 *
 * @example
 * ```jsx
 * const name = useSimpleState('Igor', 'unnamed person')
 * <p>Hello {name.val}!</p>
 * Change name: <input onChange={e => name.set(e.target.value)}/>
 * ```
 *
 * @param initialValue
 * @param defaultValue Anything to return in place of the current value if it's null or undefined
 * @param alsoEmpty {boolean} Causes the default value to be returned when the current value is falsey ('', false, 0...)
 * @return {{set: (value: any) => any, val: any, reset: () => void, merge: undefined|((extraProps: object) => void)}}
 * @see https://github.com/streamich/react-use/blob/master/src/useDefault.ts
 *
 * @note > _The "getter" function is actually a property called `val`; it doesn't pair very well with `set()`, but that happens because, well, it's a property and not a method - using `something.get` without `()` at the end would be confusingly silly._
 */
export function useSimpleState(initialValue = undefined, defaultValue = undefined, alsoEmpty = false) {
	let [state, setter] = useState(initialValue)

	if (defaultValue && (state === undefined || state === null || (alsoEmpty && !state))) {
		state = defaultValue
	}

	const result = {
		val: state,
		set: v => {
			if (v && typeof v == 'object' && 'target' in v && 'value' in v.target) { //"v && typeof" because null IS an object 🤡
				v = v.target.value
			}
			setter(v)
			return v
		},
		reset: () => setter(initialValue)
	}
	if (typeof initialValue == 'object') {
		result.merge = extraProps => setter({ ...state, ...extraProps })
	}
	return result
}
//endregion

/**
 * Manages a single `localStorage` key/value pair - serializer included!
 * @param key {string}
 * @param initialValue {*}
 * @param options {{ serializer?: function, deserializer?: function, raw?: boolean }}
 * @returns {{ val: *, set: function(v: *), del: function }}
 * @see https://github.com/streamich/react-use/blob/master/src/useLocalStorage.ts
 */
export function useLocalStorage(key, initialValue = undefined, options = undefined) {
	if (!key) {
		throw new Error('useLocalStorage()\'s key may not be falsy')
	}

	const deserializer = options? (options.raw? v => v : options.deserializer) : JSON.parse

	const initializer = useRef(key => {
		try {
			const serializer = options? (options.raw? String : options.serializer) : JSON.stringify

			const localStorageValue = localStorage.getItem(key)
			if (localStorageValue !== null) {
				return deserializer(localStorageValue)
			} else {
				initialValue && localStorage.setItem(key, serializer(initialValue))
				return initialValue
			}
		} catch {
			// If user is in private mode or has storage restriction, localStorage, JSON.parse and JSON.stringify can throw
			return initialValue
		}
	})

	const [state, setState] = useState(() => initializer.current(key))

	useLayoutEffect(() => setState(initializer.current(key)), [key])

	const set = useCallback(valOrFunc => {
			try {
				const newState = (typeof valOrFunc == 'function')? valOrFunc(state) : valOrFunc
				if (typeof newState == 'undefined') {
					return
				}
				let value

				if (options) {
					if (options.raw) {
						if (typeof newState == 'string') {
							value = newState
						} else {
							value = JSON.stringify(newState)
						}
					} else if (options.serializer) {
						value = options.serializer(newState)
					} else {
						value = JSON.stringify(newState)
					}
				} else {
					value = JSON.stringify(newState)
				}

				localStorage.setItem(key, value)
				setState(deserializer(value))
			} catch {
				// If user is in private mode or has storage restriction, localStorage, JSON.parse and JSON.stringify can throw
			}
		},
		[key, setState, options, state], //eslint-disable-line react-hooks/exhaustive-deps
	)

	const del = useCallback(() => {
		try {
			localStorage.removeItem(key)
			setState(undefined)
		} catch {
			// If user is in private mode or has storage restriction, localStorage, JSON.parse and JSON.stringify can throw
		}
	}, [key, setState])

	return {
		val: state, //not a method, so calling it `get` would be confusing; see note from useSimpleState()
		set,
		del,
	}
}
